import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  FlatList,
  StyleSheet,
  Dimensions,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  Animated,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useGoals, Goal, Payment } from '../context/GoalsContext';
import { useSettings } from '../context/SettingsContext';
import { useSubscription } from '../context/SubscriptionContext';
import { useIncome } from '../context/IncomeContext';
import { useAuth } from '../context/AuthContext';
import { createThemedStyles } from '../theme/useThemedStyles';
import { useTheme } from '../context/ThemeContext';
import { PriorityType } from '../context/GoalsContext';
import { getStyleWithError } from '../utils/styles';
import { formatCurrency } from '../utils/currency';
import { formatDate, calculateEstimatedCompletion } from '../utils/date';
import { NotificationService } from '../services/NotificationService';
import AdBanner from '../components/AdBanner';
import { parseCurrencyInput } from '../utils/currency';
import GoalLimitModal from '../components/GoalLimitModal';
import { useInterstitialAd } from '../hooks/useInterstitialAd';
import WebPage from '../components/web/WebPage';
import PageScroll from '../components/web/PageScroll';
import AppModal, { ModalFormGrid, ModalFormField } from '../components/web/AppModal';
import { isWeb } from '../utils/platformUtils';
import { webModalFooterStyle } from '../theme/webModal';
import GoalSearchFilters from '../components/GoalSearchFilters';
import { useGoalFilters } from '../hooks/useGoalFilters';

const goalTypeIcons: Record<Goal['type'], string> = {
  Savings: '💰',
  Debt: '💳',
  Investment: '📈',
};

const priorityColors: Record<PriorityType, string> = {
  Essential: '#FF4B4B',
  Important: '#FFA726',
  Optional: '#66BB6A',
};

const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const GoalsScreen = () => {
  const { goals, addGoal, updateGoal, income, addPayment, deleteGoal, deletePayment } = useGoals();
  const { settings } = useSettings();
  const { subscription, canCreateGoal, getMaxGoals } = useSubscription();
  const { colors } = useTheme();
  const styles = useGoalsStyles();

  // Show interstitial ad when screen comes into focus (rate limited to every 10 minutes)
  useInterstitialAd();
  const { user, isAuthenticated } = useAuth();
  const [modalVisible, setModalVisible] = useState(false);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState<string | null>(null);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);
  const [newGoal, setNewGoal] = useState<Partial<Goal>>({});
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [progressAnimation] = useState(new Animated.Value(0));
  const [expandedGoals, setExpandedGoals] = useState<Record<string, boolean>>({});
  const [newPayment, setNewPayment] = useState<Partial<Payment>>({
    amount: 0,
    date: getLocalDateString(),
    note: ''
  });
  const [paymentToDelete, setPaymentToDelete] = useState<{ goalId: string; paymentId: string } | null>(null);
  const [deletePaymentModalVisible, setDeletePaymentModalVisible] = useState(false);
  const [goalToRecalculate, setGoalToRecalculate] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [paymentSuccessGoalId, setPaymentSuccessGoalId] = useState<string | null>(null);
  const [formUpdateKey, setFormUpdateKey] = useState(0);
  const navigation = useNavigation();
  const route = useRoute();
  // Removed showSuccess state - using inline success messages instead
  const [aprInputValue, setAprInputValue] = useState('');
  const [goalLimitModalVisible, setGoalLimitModalVisible] = useState(false);

  const {
    searchQuery,
    setSearchQuery,
    selectedTypeFilter,
    setSelectedTypeFilter,
    selectedPriorityFilter,
    setSelectedPriorityFilter,
    selectedLoanTypeFilter,
    setSelectedLoanTypeFilter,
    selectedStatusFilter,
    setSelectedStatusFilter,
    showFilters,
    setShowFilters,
    filteredGoals,
    hasActiveFilters,
    clearFilters,
  } = useGoalFilters(goals);

  useEffect(() => {
    Animated.timing(progressAnimation, {
      toValue: 1,
      duration: 1000,
      useNativeDriver: false,
    }).start();
  }, [goals]);

  useEffect(() => {
    if (!goalToRecalculate) return;

    const goal = goals.find(g => g.id === goalToRecalculate);
    if (goal && (goal.type === 'Savings' || goal.type === 'Investment')) {
      if (
        goal.targetAmount &&
        goal.currentAmount !== undefined &&
        goal.monthlyContribution
      ) {
        const newDeadline = calculateEstimatedCompletion(goal);
        if (newDeadline && newDeadline !== goal.deadline) {
          updateGoal({ ...goal, deadline: newDeadline });
        }
      }
    }
    // Reset after running
    setGoalToRecalculate(null);
  }, [goals, goalToRecalculate]);

  // Update form state when editing a goal and its current amount changes due to payments
  useEffect(() => {
    if (editingGoal && modalVisible) {
      const updatedGoal = goals.find(g => g.id === editingGoal.id);
      if (updatedGoal && updatedGoal.currentAmount !== editingGoal.currentAmount) {
        console.log('Updating form state due to payment change:', {
          oldAmount: editingGoal.currentAmount,
          newAmount: updatedGoal.currentAmount,
          goalName: updatedGoal.name,
          goalId: updatedGoal.id
        });
        
        // Update the form state with the new current amount
        setNewGoal(prev => {
          const updated = {
            ...prev,
            currentAmount: updatedGoal.currentAmount
          };
          console.log('Updated newGoal state:', updated);
          return updated;
        });
        
        // Update the editing goal reference
        setEditingGoal(updatedGoal);
        
        // Force re-render of the form
        setFormUpdateKey(prev => prev + 1);
      }
    }
  }, [goals, editingGoal, modalVisible]);

  // Monitor credit card goal changes for debugging
  useEffect(() => {
    const creditCardGoals = goals.filter(g => g.type === 'Debt' && g.loanTermYears === undefined);
    if (creditCardGoals.length > 0) {
      console.log('Credit Card Goals State Update:', creditCardGoals.map(g => ({
        name: g.name,
        currentAmount: g.currentAmount,
        deadline: g.deadline,
        paymentsCount: g.payments.length,
        lastUpdated: g.lastUpdated
      })));
    }
  }, [goals]);

  // Calculate deadline based on current progress and monthly contribution
  const calculateDeadline = useCallback((currentAmount: number, targetAmount: number, monthlyContribution: number) => {
    if (!monthlyContribution || monthlyContribution <= 0) return '';
    const remainingAmount = targetAmount - currentAmount;
    const monthsNeeded = Math.ceil(remainingAmount / monthlyContribution);
    const deadlineDate = new Date();
    deadlineDate.setMonth(deadlineDate.getMonth() + monthsNeeded);
    return deadlineDate.toISOString().split('T')[0];
  }, []);

  const calculateDebtWithInterest = (
    currentBalance: number,
    targetAmount: number,
    monthlyPayment: number,
    apr: number,
    loanTermYears?: number,
    loanTermMonths?: number
  ) => {
    // For fixed-term loans (mortgage, car loan, etc.)
    if (loanTermYears !== undefined) {
      const totalMonths = (loanTermYears * 12) + (loanTermMonths || 0);
      const monthlyRate = apr / 100 / 12;
      
      // Calculate remaining balance
      const remainingBalance = targetAmount - currentBalance;
      
      // Calculate monthly payment using loan amortization formula
      // PMT = P * (r * (1 + r)^n) / ((1 + r)^n - 1)
      const calculatedMonthlyPayment = remainingBalance * 
        (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
        (Math.pow(1 + monthlyRate, totalMonths) - 1);

      // Calculate total amount with interest
      const totalPayments = calculatedMonthlyPayment * totalMonths;
      const totalAmount = currentBalance + totalPayments;
      const totalInterest = totalPayments - remainingBalance;

      return {
        totalAmount: Math.round(totalAmount * 100) / 100,
        monthlyPayment: Math.round(calculatedMonthlyPayment * 100) / 100,
        totalInterest: Math.round(totalInterest * 100) / 100,
        months: totalMonths,
        isFixedTerm: true
      };
    }
    
    // Handle credit card debt using daily periodic rate
    else {
      // Calculate months needed to pay off the debt with given monthly payment
      const remainingBalance = targetAmount - currentBalance;
      const months = Math.ceil(remainingBalance / monthlyPayment);
      const totalDays = months * 30; // Approximate days based on months
      
      // Calculate daily periodic rate
      const dailyRate = apr / 100 / 365;
      
      // Calculate daily interest amount on the remaining balance
      const dailyInterest = remainingBalance * dailyRate;
      
      // Calculate total interest over the estimated period
      const totalInterest = dailyInterest * totalDays;
      
      // Total amount is current balance plus remaining balance plus total interest
      const totalAmount = currentBalance + remainingBalance + totalInterest;

      return {
        totalAmount: Math.round(totalAmount * 100) / 100,
        monthlyPayment: Math.round(monthlyPayment * 100) / 100,
        totalInterest: Math.round(totalInterest * 100) / 100,
        months: months,
        dailyInterest: Math.round(dailyInterest * 100) / 100,
        isFixedTerm: false
      };
    }
  };

  const calculateSavingsWithInterest = (currentBalance: number, targetAmount: number, monthlyContribution: number, apr: number) => {
    const monthlyRate = apr / 100 / 12;
    let balance = currentBalance;
    let months = 0;
    
    // Keep track of the original target without interest
    const originalTarget = targetAmount;
    
    // Calculate until we reach or exceed the target amount
    while (balance < targetAmount && months < 600) { // Cap at 50 years
      // Add monthly contribution
      balance += monthlyContribution;
      
      // Add monthly interest on the entire balance
      balance += (balance * monthlyRate);
      
      months++;
      
      // Update target amount with earned interest
      if (months % 12 === 0) { // Recalculate target annually
        targetAmount = originalTarget + (balance - currentBalance - (monthlyContribution * months));
      }
    }

    return {
      totalAmount: Math.ceil(balance), // Round up to nearest dollar
      months,
      originalTargetAmount: originalTarget
    };
  };

  const updateFixedTermLoanCalculations = () => {
    setNewGoal(prev => {
      if (prev.type === 'Debt' && prev.loanTermYears !== undefined && prev.apr && prev.originalTargetAmount) {
        const P = Number(prev.originalTargetAmount);
        const r = Number(prev.apr) / 100 / 12;
        const n = (Number(prev.loanTermYears) * 12) + (Number(prev.loanTermMonths) || 0);
        
        if (P > 0 && r > 0 && n > 0) {
          // Calculate monthly payment using loan amortization formula
          const monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
          
          // Calculate total amount with interest
          const totalPaid = monthlyPayment * n;
          const totalInterest = totalPaid - P;
          const targetAmount = P + totalInterest;

          // Update the goal with calculated values
          return {
            ...prev,
            targetAmount: targetAmount,
            monthlyContribution: monthlyPayment,
            allocatedPercentage: income.monthlyIncome > 0 ? (monthlyPayment / income.monthlyIncome) * 100 : 0
          };
        }
      }
      return prev;
    });
  };

  const handleAmountChange = (field: 'targetAmount' | 'currentAmount' | 'originalTargetAmount', value: number) => {
    console.log(`handleAmountChange called with field: ${field}, value: ${value}`);
    
    setNewGoal(prev => {
      const updatedGoal = { ...prev, [field]: value };
      
      // For fixed-term loans, update calculations when original amount changes
      if (field === 'originalTargetAmount' && updatedGoal.type === 'Debt' && updatedGoal.loanTermYears !== undefined) {
        // We'll call updateFixedTermLoanCalculations after the state update
        setTimeout(() => updateFixedTermLoanCalculations(), 0);
      }
      
      return updatedGoal;
    });
  };

  const handleTargetAmountBlur = () => {
    setNewGoal(prev => {
      // Only proceed if this is a debt goal with APR
      if (prev.type !== 'Debt' || !prev.apr || !prev.targetAmount) {
        return prev;
      }

      const updatedGoal = { ...prev };
      updatedGoal.originalTargetAmount = prev.targetAmount;

      const currentAmt = updatedGoal.currentAmount || 0;

      if (updatedGoal.loanTermYears !== undefined && updatedGoal.targetAmount !== undefined && updatedGoal.apr !== undefined && updatedGoal.monthlyContribution !== undefined) {
        // For fixed-term loans
        const { totalAmount, monthlyPayment, months } = calculateDebtWithInterest(
          currentAmt,
          updatedGoal.targetAmount,
          updatedGoal.monthlyContribution,
          updatedGoal.apr,
          updatedGoal.loanTermYears,
          updatedGoal.loanTermMonths
        );

        updatedGoal.targetAmount = totalAmount;
        updatedGoal.monthlyContribution = monthlyPayment;

        // Update deadline based on new calculations
        const estimatedDate = new Date();
        estimatedDate.setMonth(estimatedDate.getMonth() + months);
        updatedGoal.deadline = estimatedDate.toISOString().split('T')[0];
      } else if (updatedGoal.targetAmount !== undefined && updatedGoal.apr !== undefined) {
        // For credit card debt
        const remainingBalance = updatedGoal.targetAmount - currentAmt;
        const minPayment = Math.max(remainingBalance * 0.02, 25);
        
        const { totalAmount } = calculateDebtWithInterest(
          currentAmt,
          updatedGoal.targetAmount,
          minPayment,
          updatedGoal.apr
        );

        updatedGoal.targetAmount = totalAmount;
      }

      return updatedGoal;
    });
  };

  const handleMonthlyContributionChange = (amount: number) => {
    setNewGoal(prev => {
      const updatedGoal = {
        ...prev,
        monthlyContribution: amount,
        allocatedPercentage: income.monthlyIncome > 0 ? (amount / income.monthlyIncome) * 100 : 0
      };

      // Recalculate interest if this is a debt goal with APR
      if (updatedGoal.type === 'Debt' && updatedGoal.apr !== undefined && updatedGoal.currentAmount !== undefined) {
        const originalAmount = updatedGoal.originalTargetAmount ?? updatedGoal.targetAmount;

        if (updatedGoal.loanTermYears !== undefined && originalAmount !== undefined && updatedGoal.apr !== undefined) {
          // For fixed-term loans
          const { totalAmount, monthlyPayment, months } = calculateDebtWithInterest(
            updatedGoal.currentAmount,
            originalAmount,
            amount,
            updatedGoal.apr,
            updatedGoal.loanTermYears,
            updatedGoal.loanTermMonths
          );

          updatedGoal.targetAmount = totalAmount;
          updatedGoal.monthlyContribution = monthlyPayment;

          // Update deadline based on new calculations
          const estimatedDate = new Date();
          estimatedDate.setMonth(estimatedDate.getMonth() + months);
          updatedGoal.deadline = estimatedDate.toISOString().split('T')[0];
        } else if (originalAmount !== undefined && updatedGoal.apr !== undefined) {
          // For credit card debt
          const { totalAmount } = calculateDebtWithInterest(
            updatedGoal.currentAmount,
            originalAmount,
            amount,
            updatedGoal.apr
          );

          updatedGoal.targetAmount = totalAmount;
        }
      }
      
      // Recalculate deadline if we have all required values
      if (updatedGoal.targetAmount && 
          updatedGoal.currentAmount !== undefined && 
          updatedGoal.monthlyContribution) {
        updatedGoal.deadline = calculateEstimatedCompletion(updatedGoal as Goal) || prev.deadline;
      }
      
      return updatedGoal;
    });
  };

  const handleAllocationChange = (percentage: number) => {
    if (percentage < 0 || percentage > 100) return;

    setNewGoal(prev => {
      const updatedGoal = {
        ...prev,
        allocatedPercentage: percentage,
        monthlyContribution: (income.monthlyIncome * percentage) / 100
      };
      
      // Recalculate deadline if we have all required values
      if (updatedGoal.targetAmount && 
          updatedGoal.currentAmount !== undefined && 
          updatedGoal.monthlyContribution) {
        updatedGoal.deadline = calculateEstimatedCompletion(updatedGoal as Goal) || prev.deadline;
      }
      
      return updatedGoal;
    });
  };

  const handleLoanTermChange = (type: 'years' | 'months', value: number) => {
    setNewGoal(prev => {
      const updatedGoal = {
        ...prev,
        [type === 'years' ? 'loanTermYears' : 'loanTermMonths']: value
      };
      
      // For fixed-term loans, update calculations when loan terms change
      if (updatedGoal.type === 'Debt' && updatedGoal.loanTermYears !== undefined) {
        // We'll call updateFixedTermLoanCalculations after the state update
        setTimeout(() => updateFixedTermLoanCalculations(), 0);
      }
      
      return updatedGoal;
    });
  };

  const handleAprChange = (apr: number) => {
    console.log('handleAprChange called with apr:', apr);
    if (apr < 0 || apr > 100) return;

    setNewGoal(prev => {
      console.log('handleAprChange - previous state:', prev);
      
      // If APR is being cleared or is invalid, reset only interest-related calculations
      if (apr === 0 || isNaN(apr)) {
        console.log('handleAprChange - clearing APR, resetting calculations');
        setAprInputValue('');
        return {
          ...prev,
          apr: undefined,
          // Keep target amount as original amount (without interest)
          targetAmount: prev.originalTargetAmount || prev.targetAmount,
          // Keep monthly contribution based on original amount
          monthlyContribution: prev.originalTargetAmount ? Math.max((prev.originalTargetAmount - (prev.currentAmount || 0)) * 0.02, 25) : prev.monthlyContribution,
          // Reset only interest-related fields
          originalTargetAmount: undefined,
          deadline: undefined
        };
      }

      const updatedGoal = {
        ...prev,
        apr
      };

      console.log('handleAprChange - updatedGoal after setting apr:', updatedGoal);

      // Store original target amount if not already stored
      if (!updatedGoal.originalTargetAmount && updatedGoal.targetAmount) {
        updatedGoal.originalTargetAmount = updatedGoal.targetAmount;
        console.log('handleAprChange - stored originalTargetAmount:', updatedGoal.originalTargetAmount);
      }

      // For fixed-term loans, update calculations
      if (updatedGoal.type === 'Debt' && updatedGoal.loanTermYears !== undefined) {
        // We'll call updateFixedTermLoanCalculations after the state update
        setTimeout(() => updateFixedTermLoanCalculations(), 0);
      }
      // For credit card loans, keep existing logic
      else if (updatedGoal.type === 'Debt' && updatedGoal.originalTargetAmount && updatedGoal.currentAmount !== undefined) {
        console.log('handleAprChange - calculating credit card debt');
        // For credit card debt - use proper minimum payment calculation if monthlyContribution is not set
        const remainingBalance = updatedGoal.originalTargetAmount - updatedGoal.currentAmount;
        const minPayment = Math.max(remainingBalance * 0.02, 25); // 2% of balance or $25 minimum
        const paymentToUse = updatedGoal.monthlyContribution ?? minPayment;
        
        console.log('handleAprChange - credit card calculation inputs:', {
          remainingBalance,
          minPayment,
          paymentToUse
        });
        
        const { totalAmount } = calculateDebtWithInterest(
          updatedGoal.currentAmount,
          updatedGoal.originalTargetAmount,
          paymentToUse,
          apr
        );

        // Update goal with new calculations
        updatedGoal.targetAmount = totalAmount;
        updatedGoal.monthlyContribution = minPayment;
        updatedGoal.allocatedPercentage = income.monthlyIncome > 0 ? (minPayment / income.monthlyIncome) * 100 : 0;
        
        console.log('handleAprChange - credit card results:', {
          totalAmount,
          minPayment,
          allocatedPercentage: updatedGoal.allocatedPercentage
        });
      }
      
      console.log('handleAprChange - final updatedGoal:', updatedGoal);
      return updatedGoal;
    });
  };

  // Helper function to create a Firestore-safe goal object (omits undefined values)
  const createFirestoreSafeGoal = (goal: any, isNewGoal: boolean = false) => {
    const baseGoal = {
      name: goal.name,
      type: goal.type,
      targetAmount: goal.targetAmount,
      currentAmount: goal.currentAmount,
      deadline: goal.deadline,
      monthlyContribution: goal.monthlyContribution,
      priority: goal.priority,
      allocatedPercentage: goal.allocatedPercentage,
      payments: goal.payments || [],
      createdAt: isNewGoal ? new Date().toISOString() : goal.createdAt,
      lastUpdated: new Date().toISOString(),
    };

    // Add optional fields only if they have values (not undefined)
    const optionalFields: any = {};
    
    if (goal.apr !== undefined) optionalFields.apr = goal.apr;
    if (goal.originalTargetAmount !== undefined) optionalFields.originalTargetAmount = goal.originalTargetAmount;
    if (goal.loanTermYears !== undefined) optionalFields.loanTermYears = goal.loanTermYears;
    if (goal.loanTermMonths !== undefined) optionalFields.loanTermMonths = goal.loanTermMonths;
    if (goal.startDate !== undefined) optionalFields.startDate = goal.startDate;
    
    // For completedDate, use null instead of undefined for Firestore
    optionalFields.completedDate = goal.completedDate || null;

    const firestoreGoal = { ...baseGoal, ...optionalFields };
    
    // Debug logging for credit card goals
    if (goal.type === 'Debt' && goal.loanTermYears === undefined) {
      console.log('Firestore Goal Being Saved:', {
        goalName: goal.name,
        targetAmount: firestoreGoal.targetAmount,
        monthlyContribution: firestoreGoal.monthlyContribution,
        allocatedPercentage: firestoreGoal.allocatedPercentage,
        originalTargetAmount: firestoreGoal.originalTargetAmount,
        apr: firestoreGoal.apr
      });
    }

    return firestoreGoal;
  };

  const validateForm = () => {
    console.log('=== VALIDATE FORM DEBUG START ===');
    const errors: Record<string, string> = {};

    // Check required fields
    if (!newGoal.priority) {
      errors.priority = 'Priority is required';
      console.log('Priority validation failed');
    }
    if (!newGoal.type) {
      errors.type = 'Goal type is required';
      console.log('Type validation failed');
    }
    if (!newGoal.name || newGoal.name.trim() === '') {
      errors.name = 'Goal name is required';
      console.log('Name validation failed');
    }

    // Check amounts
    if (newGoal.targetAmount === undefined || newGoal.targetAmount <= 0) {
      errors.targetAmount = 'Target amount must be greater than 0';
      console.log('Target amount validation failed:', newGoal.targetAmount);
    }
    if (newGoal.monthlyContribution === undefined || newGoal.monthlyContribution <= 0) {
      errors.monthlyContribution = 'Monthly contribution must be greater than 0';
      console.log('Monthly contribution validation failed:', newGoal.monthlyContribution);
    }

    // Check APR for debt goals
    if (newGoal.type === 'Debt' && (!newGoal.apr || newGoal.apr <= 0)) {
      errors.apr = 'APR is required for debt goals';
      console.log('APR validation failed for debt goal');
    }

    // Check original amount for fixed-term loans
    if (newGoal.type === 'Debt' && newGoal.loanTermYears !== undefined && (!newGoal.originalTargetAmount || newGoal.originalTargetAmount <= 0)) {
      errors.originalTargetAmount = 'Original amount is required for fixed-term loans';
      console.log('Original amount validation failed for fixed-term loan');
    }

    // Check original amount for credit card goals
    if (newGoal.type === 'Debt' && newGoal.loanTermYears === undefined && (!newGoal.originalTargetAmount || newGoal.originalTargetAmount <= 0)) {
      errors.originalTargetAmount = 'Original amount is required for credit card goals';
      console.log('Original amount validation failed for credit card goal');
    }

    // Check current amount for fixed-term loans
    if (newGoal.type === 'Debt' && newGoal.loanTermYears !== undefined && (newGoal.currentAmount === undefined || newGoal.currentAmount < 0)) {
      errors.currentAmount = 'Total paid to date is required for fixed-term loans';
      console.log('Current amount validation failed for fixed-term loan');
    }

    // Check current amount for credit card goals
    if (newGoal.type === 'Debt' && newGoal.loanTermYears === undefined && (newGoal.currentAmount === undefined || newGoal.currentAmount < 0)) {
      errors.currentAmount = 'Total paid to date is required for credit card goals';
      console.log('Current amount validation failed for credit card goal');
    }

    // Check loan terms for fixed-term loans
    if (newGoal.type === 'Debt' && newGoal.loanTermYears !== undefined) {
      if (!newGoal.loanTermYears || newGoal.loanTermYears <= 0) {
        errors.loanTermYears = 'Loan term years must be greater than 0';
        console.log('Loan term years validation failed');
      }
    }

    console.log('Validation errors:', errors);
    console.log('Form data being validated:', newGoal);
    
    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    console.log('Form is valid:', isValid);
    
    if (!isValid) {
      // Removed alert popup for form validation failure
    }
    
    return isValid;
  };

  const renderCreditCardFields = () => {
    const P = Number(newGoal.originalTargetAmount) || 0;
    // For credit card debt, currentAmount represents how much has been paid
    // The remaining balance is originalTargetAmount - currentAmount
    const totalPaid = Number(newGoal.currentAmount) || 0;
    const remainingBalance = P - totalPaid;
    const apr = Number(newGoal.apr) || 0;
    
    console.log('Credit Card Modal Calculation Inputs:', {
      originalAmount: P,
      totalPaid: totalPaid,
      remainingBalance: remainingBalance,
      apr: apr,
      monthlyIncome: income.monthlyIncome,
      newGoal: newGoal,
      editingGoal: editingGoal,
      isEditing: !!editingGoal
    });
    
    // Use remaining balance for calculations (this is the actual debt amount)
    const creditCardCalc = calculateCreditCardGoal(P, totalPaid, apr, income.monthlyIncome, newGoal.payments, newGoal.createdAt);
    
    console.log('Credit Card Modal Calculation Results:', {
      recommendedMonthlyPayment: creditCardCalc.recommendedMonthlyPayment,
      targetAmount: creditCardCalc.targetAmount,
      totalInterest: creditCardCalc.totalInterest,
      allocationPercent: creditCardCalc.allocationPercent,
      payoffDate: creditCardCalc.payoffDate
    });
    
    if (remainingBalance <= 0 && P > 0) {
      // Already paid off
      return (
        <View>
          <TextInput
            style={getStyleWithError(styles.input, validationErrors.originalTargetAmount, styles.inputError)}
            placeholder="Original Amount"
            keyboardType="numeric"
            value={newGoal.originalTargetAmount?.toString()}
            onChangeText={(text) => {
              const amount = parseCurrencyInput(text);
              handleAmountChange('originalTargetAmount', amount);
              if (validationErrors.originalTargetAmount) {
                setValidationErrors(prev => ({ ...prev, originalTargetAmount: '' }));
              }
            }}
          />
          {validationErrors.originalTargetAmount ? <Text style={styles.errorText}>{validationErrors.originalTargetAmount}</Text> : null}
          {newGoal.originalTargetAmount ? <Text style={styles.inputLabel}>Original Amount</Text> : null}
          <TextInput style={styles.input} placeholder="Start Date" value={newGoal.startDate} onChangeText={(text) => setNewGoal(prev => ({ ...prev, startDate: text }))} />
          {newGoal.startDate ? <Text style={styles.inputLabel}>Start Date</Text> : null}
          <TextInput
            style={getStyleWithError(styles.input, validationErrors.currentAmount, styles.inputError)}
            placeholder="Total Paid to Date"
            keyboardType="numeric"
            value={newGoal.currentAmount?.toString()}
            onChangeText={(text) => {
              const amount = parseCurrencyInput(text);
              handleAmountChange('currentAmount', amount);
              if (validationErrors.currentAmount) {
                setValidationErrors(prev => ({ ...prev, currentAmount: '' }));
              }
            }}
          />
          {validationErrors.currentAmount ? <Text style={styles.errorText}>{validationErrors.currentAmount}</Text> : null}
          {newGoal.currentAmount ? <Text style={styles.inputLabel}>Total Paid to Date</Text> : null}
          <TextInput 
            style={getStyleWithError(styles.input, validationErrors.apr, styles.inputError)} 
            placeholder="Interest Rate (APR %)" 
            keyboardType="numeric" 
            value={aprInputValue} 
            onChangeText={(text) => { 
              setAprInputValue(text); 
              const apr = parseFloat(text); 
              handleAprChange(isNaN(apr) ? 0 : apr); 
              if (validationErrors.apr) { 
                setValidationErrors(prev => ({ ...prev, apr: '' })); 
              } 
            }} 
          />
          {validationErrors.apr ? <Text style={styles.errorText}>{validationErrors.apr}</Text> : null}
          {aprInputValue ? <Text style={styles.inputLabel}>Interest Rate (APR %)</Text> : null}
          <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Recommended Monthly Payment (Auto-Calculated, 50% above minimum)" value={creditCardCalc.recommendedMonthlyPayment ? formatCurrency(creditCardCalc.recommendedMonthlyPayment, settings.currency.code, settings.currency.symbol) : ''} editable={false} />
          {creditCardCalc.recommendedMonthlyPayment > 0 ? <Text style={styles.inputLabel}>Recommended Monthly Payment (Auto-Calculated, 50% above minimum)</Text> : null}
          <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Monthly Allocation % (Auto-Calculated)" value={creditCardCalc.allocationPercent ? creditCardCalc.allocationPercent.toFixed(2) : ''} editable={false} />
          {creditCardCalc.allocationPercent > 0 ? <Text style={styles.inputLabel}>Monthly Allocation % (Auto-Calculated)</Text> : null}
          <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Target Amount (Auto-Calculated)" value={creditCardCalc.targetAmount ? formatCurrency(creditCardCalc.targetAmount, settings.currency.code, settings.currency.symbol) : ''} editable={false} />
          {creditCardCalc.targetAmount > 0 ? <Text style={styles.inputLabel}>Target Amount (Auto-Calculated)</Text> : null}
          <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Total Interest (Auto-Calculated)" value={creditCardCalc.totalInterest ? formatCurrency(creditCardCalc.totalInterest, settings.currency.code, settings.currency.symbol) : ''} editable={false} />
          {creditCardCalc.totalInterest > 0 ? <Text style={styles.inputLabel}>Total Interest (Auto-Calculated)</Text> : null}
          <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Estimated Completion Date (Auto-Calculated)" value={creditCardCalc.payoffDate || undefined} editable={false} />
          {creditCardCalc.payoffDate ? <Text style={styles.inputLabel}>Estimated Completion Date (Auto-Calculated)</Text> : null}
        </View>
      );
    }
    return (
      <View>
        <TextInput
          style={getStyleWithError(styles.input, validationErrors.originalTargetAmount, styles.inputError)}
          placeholder="Original Amount"
          keyboardType="numeric"
          value={newGoal.originalTargetAmount?.toString()}
          onChangeText={(text) => {
            const amount = parseCurrencyInput(text);
            handleAmountChange('originalTargetAmount', amount);
            if (validationErrors.originalTargetAmount) {
              setValidationErrors(prev => ({ ...prev, originalTargetAmount: '' }));
            }
          }}
        />
        {validationErrors.originalTargetAmount ? <Text style={styles.errorText}>{validationErrors.originalTargetAmount}</Text> : null}
        {newGoal.originalTargetAmount ? <Text style={styles.inputLabel}>Original Amount</Text> : null}
        <TextInput style={styles.input} placeholder="Start Date" value={newGoal.startDate} onChangeText={(text) => setNewGoal(prev => ({ ...prev, startDate: text }))} />
        {newGoal.startDate ? <Text style={styles.inputLabel}>Start Date</Text> : null}
        <TextInput
          style={getStyleWithError(styles.input, validationErrors.currentAmount, styles.inputError)}
          placeholder="Total Paid to Date"
          keyboardType="numeric"
          value={newGoal.currentAmount?.toString()}
          onChangeText={(text) => {
            const amount = parseCurrencyInput(text);
            handleAmountChange('currentAmount', amount);
            if (validationErrors.currentAmount) {
              setValidationErrors(prev => ({ ...prev, currentAmount: '' }));
            }
          }}
        />
        {validationErrors.currentAmount ? <Text style={styles.errorText}>{validationErrors.currentAmount}</Text> : null}
        {newGoal.currentAmount ? <Text style={styles.inputLabel}>Total Paid to Date</Text> : null}
        <TextInput 
          style={getStyleWithError(styles.input, validationErrors.apr, styles.inputError)} 
          placeholder="Interest Rate (APR %)" 
          keyboardType="numeric" 
          value={aprInputValue} 
          onChangeText={(text) => { 
            setAprInputValue(text); 
            const apr = parseFloat(text); 
            handleAprChange(isNaN(apr) ? 0 : apr); 
            if (validationErrors.apr) { 
              setValidationErrors(prev => ({ ...prev, apr: '' })); 
            } 
          }} 
        />
        {validationErrors.apr ? <Text style={styles.errorText}>{validationErrors.apr}</Text> : null}
        {aprInputValue ? <Text style={styles.inputLabel}>Interest Rate (APR %)</Text> : null}
        <TextInput
          style={[styles.input, styles.autoCalculatedInput]}
          placeholder="Recommended Monthly Payment (Auto-Calculated, 50% above minimum)"
          value={creditCardCalc.recommendedMonthlyPayment ? formatCurrency(creditCardCalc.recommendedMonthlyPayment, settings.currency.code, settings.currency.symbol) : ''}
          editable={false}
        />
        {creditCardCalc.recommendedMonthlyPayment > 0 ? <Text style={styles.inputLabel}>Recommended Monthly Payment (Auto-Calculated, 50% above minimum)</Text> : null}
        <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Monthly Allocation % (Auto-Calculated)" value={creditCardCalc.allocationPercent ? creditCardCalc.allocationPercent.toFixed(2) : ''} editable={false} />
        {creditCardCalc.allocationPercent > 0 ? <Text style={styles.inputLabel}>Monthly Allocation % (Auto-Calculated)</Text> : null}
        <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Target Amount (Auto-Calculated)" value={creditCardCalc.targetAmount ? formatCurrency(creditCardCalc.targetAmount, settings.currency.code, settings.currency.symbol) : ''} editable={false} />
        {creditCardCalc.targetAmount > 0 ? <Text style={styles.inputLabel}>Target Amount (Auto-Calculated)</Text> : null}
        <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Total Interest (Auto-Calculated)" value={creditCardCalc.totalInterest ? formatCurrency(creditCardCalc.totalInterest, settings.currency.code, settings.currency.symbol) : ''} editable={false} />
        {creditCardCalc.totalInterest > 0 ? <Text style={styles.inputLabel}>Total Interest (Auto-Calculated)</Text> : null}
        <TextInput style={[styles.input, styles.autoCalculatedInput]} placeholder="Estimated Completion Date (Auto-Calculated)" value={creditCardCalc.payoffDate || undefined} editable={false} />
        {creditCardCalc.payoffDate ? <Text style={styles.inputLabel}>Estimated Completion Date (Auto-Calculated)</Text> : null}
      </View>
    );
  };

  const handleSaveGoal = async () => {
    try {
      console.log('=== SAVE GOAL DEBUG START ===');
      console.log('Saving goal:', {
        isEditing: !!editingGoal,
        newGoal,
        editingGoal,
        subscription: subscription,
        canCreateGoal: canCreateGoal,
        getMaxGoals: getMaxGoals,
        currentGoalsCount: goals.length
      });

      // Check authentication state
      console.log('Authentication state:', {
        currentUser: user,
        isAuthenticated: isAuthenticated,
        userId: user?.uid
      });

      if (!isAuthenticated || !user) {
        console.error('No authenticated user found');
        return;
      }

      // Validate form before saving
      if (!validateForm()) {
        console.log('Form validation failed');
        return;
      }

      // Check subscription limits for new goals only
      if (!editingGoal) {
        console.log('Checking subscription limits for new goal...');
        console.log('canCreateGoal function:', typeof canCreateGoal);
        console.log('getMaxGoals function:', typeof getMaxGoals);
        
        if (canCreateGoal && getMaxGoals) {
          const canCreate = canCreateGoal(goals.length);
          const maxGoals = getMaxGoals();
          console.log('Subscription check results:', { canCreate, maxGoals, currentCount: goals.length });
          
          if (!canCreate) {
            console.log('Goal limit reached, showing upgrade modal');
            setGoalLimitModalVisible(true);
            return;
          }
        } else {
          console.log('Subscription functions not available, proceeding with save');
        }
      }

      // For editing, ensure we have all the required fields from either newGoal or editingGoal
      let goalToSave = editingGoal ? {
        ...editingGoal,
        ...newGoal,
        // Explicitly handle required fields
        priority: newGoal.priority || editingGoal.priority,
        type: newGoal.type || editingGoal.type,
        name: newGoal.name || editingGoal.name,
        // Use ?? for numbers that could be 0
        currentAmount: editingGoal.payments?.length > 0 ? editingGoal.currentAmount : (newGoal.currentAmount ?? editingGoal.currentAmount),
        targetAmount: newGoal.targetAmount ?? editingGoal.targetAmount,
        monthlyContribution: newGoal.monthlyContribution ?? editingGoal.monthlyContribution,
        allocatedPercentage: newGoal.allocatedPercentage ?? editingGoal.allocatedPercentage,
        // Handle optional fields - ensure they are undefined instead of null
        apr: newGoal.apr ?? editingGoal.apr ?? undefined,
        originalTargetAmount: newGoal.originalTargetAmount ?? editingGoal.originalTargetAmount ?? undefined,
        loanTermYears: newGoal.loanTermYears ?? editingGoal.loanTermYears ?? undefined,
        loanTermMonths: newGoal.loanTermMonths ?? editingGoal.loanTermMonths ?? undefined,
        deadline: newGoal.deadline || editingGoal.deadline || new Date().toISOString().split('T')[0], // Ensure deadline is never null
        payments: editingGoal.payments || [],
        lastUpdated: new Date().toISOString()
      } : {
        ...newGoal,
        // For new goals, ensure optional fields are undefined instead of null
        apr: newGoal.apr ?? undefined,
        originalTargetAmount: newGoal.originalTargetAmount ?? undefined,
        loanTermYears: newGoal.loanTermYears ?? undefined,
        loanTermMonths: newGoal.loanTermMonths ?? undefined,
        deadline: newGoal.deadline || new Date().toISOString().split('T')[0], // Ensure deadline is never null
        payments: [],
        lastUpdated: new Date().toISOString()
      };

      // For fixed term loans, calculate targetAmount and monthlyContribution from loan terms
      if (goalToSave.type === 'Debt' && goalToSave.loanTermYears !== undefined && goalToSave.apr && goalToSave.originalTargetAmount) {
        const P = Number(goalToSave.originalTargetAmount);
        const r = Number(goalToSave.apr) / 100 / 12;
        const n = (Number(goalToSave.loanTermYears) * 12) + (Number(goalToSave.loanTermMonths) || 0);
        
        if (P > 0 && r > 0 && n > 0) {
          // Calculate monthly payment using loan amortization formula
          const monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
          
          // Calculate total amount with interest
          const totalPaid = monthlyPayment * n;
          const totalInterest = totalPaid - P;
          const targetAmount = P + totalInterest;

          // Update the goal with calculated values
          goalToSave = {
            ...goalToSave,
            targetAmount: targetAmount,
            monthlyContribution: monthlyPayment,
            allocatedPercentage: income.monthlyIncome > 0 ? (monthlyPayment / income.monthlyIncome) * 100 : 0
          };
        }
      }

      // For credit card goals, calculate targetAmount and monthlyContribution using the shared function
      if (goalToSave.type === 'Debt' && goalToSave.loanTermYears === undefined && goalToSave.apr && goalToSave.originalTargetAmount) {
        const P = Number(goalToSave.originalTargetAmount);
        const currentAmt = Number(goalToSave.currentAmount) || 0;
        const apr = Number(goalToSave.apr);
        const creditCardCalc = calculateCreditCardGoal(P, currentAmt, apr, income.monthlyIncome, goalToSave.payments, goalToSave.createdAt);
        
        console.log('Credit Card Save Calculation:', {
          originalAmount: P,
          currentAmount: currentAmt,
          apr: apr,
          calculatedTargetAmount: creditCardCalc.targetAmount,
          calculatedMonthlyPayment: creditCardCalc.recommendedMonthlyPayment,
          calculatedAllocationPercent: creditCardCalc.allocationPercent,
          calculatedTotalInterest: creditCardCalc.totalInterest,
          calculatedPayoffDate: creditCardCalc.payoffDate
        });
        
        goalToSave = {
          ...goalToSave,
          targetAmount: creditCardCalc.targetAmount,
          monthlyContribution: creditCardCalc.recommendedMonthlyPayment,
          allocatedPercentage: creditCardCalc.allocationPercent,
          deadline: creditCardCalc.payoffDate, // Add the calculated payoff date as deadline
        };
        
        console.log('Credit Card Goal After Save Calculation:', {
          targetAmount: goalToSave.targetAmount,
          monthlyContribution: goalToSave.monthlyContribution,
          allocatedPercentage: goalToSave.allocatedPercentage
        });
      }

      // Additional fallback for credit card goals if the above didn't work
      if (goalToSave.type === 'Debt' && goalToSave.targetAmount === undefined && goalToSave.originalTargetAmount) {
        console.log('Fallback credit card calculation triggered');
        goalToSave = {
          ...goalToSave,
          targetAmount: Number(goalToSave.originalTargetAmount),
          monthlyContribution: goalToSave.monthlyContribution || 25 // Default minimum payment
        };
      }

      // Log validation state
      const validationState = {
        hasPriority: !!goalToSave.priority,
        hasType: !!goalToSave.type,
        hasName: !!goalToSave.name,
        hasTargetAmount: goalToSave.targetAmount !== undefined,
        hasMonthlyContribution: goalToSave.monthlyContribution !== undefined,
        priority: goalToSave.priority,
        targetAmount: goalToSave.targetAmount,
        monthlyContribution: goalToSave.monthlyContribution,
        originalTargetAmount: goalToSave.originalTargetAmount,
        apr: goalToSave.apr,
        loanTermYears: goalToSave.loanTermYears,
        goal: goalToSave
      };
      console.log('Validation state:', validationState);

      // Validate required fields
      const missingFields = [];
      if (!goalToSave.priority) missingFields.push('Priority');
      if (!goalToSave.type) missingFields.push('Type');
      if (!goalToSave.name) missingFields.push('Name');
      if (goalToSave.targetAmount === undefined) missingFields.push('Target Amount');
      if (goalToSave.monthlyContribution === undefined) missingFields.push('Monthly Contribution');

      if (missingFields.length > 0) {
        throw new Error(`Please fill in the following required fields: ${missingFields.join(', ')}`);
      }

      // Save the goal
      if (editingGoal) {
        // For editing, ensure we have a proper Goal object with id
        const goalForUpdate: Goal = {
          id: editingGoal.id,
          ...createFirestoreSafeGoal(goalToSave, false)
        };
        console.log('Updating goal with:', goalForUpdate);
        await updateGoal(goalForUpdate);
      } else {
        // For new goals, ensure we have a proper Omit<Goal, 'id'> object
        const goalForAdd: Omit<Goal, 'id'> = createFirestoreSafeGoal(goalToSave, true);
        console.log('Adding new goal:', goalForAdd);
        await addGoal(goalForAdd);
      }

      setModalVisible(false);
      // Don't clear newGoal - let the calculated values remain visible
      setEditingGoal(null);
      console.log('=== SAVE GOAL DEBUG END - SUCCESS ===');
    } catch (error) {
      if (error instanceof Error && error.message && error.message.toLowerCase().includes('goal limit')) {
        setGoalLimitModalVisible(true);
      } else {
        console.error('=== SAVE GOAL DEBUG END - ERROR ===');
        console.error('Error saving goal:', error);
        console.error('Error details:', {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : 'No stack trace',
          errorType: typeof error,
          errorObject: error
        });
        Alert.alert('Error', error instanceof Error ? error.message : 'Failed to save goal');
      }
    }
  };

  // When opening the edit modal, properly initialize newGoal with existing values
  const handleEditGoal = (goal: Goal) => {
    // Get the latest goal data from the goals array to ensure we have the most up-to-date information
    const latestGoal = goals.find(g => g.id === goal.id) || goal;
    console.log('Editing goal:', latestGoal);
    console.log('Original goal passed:', goal);
    console.log('Latest goal from array:', latestGoal);
    
    setEditingGoal(latestGoal);
    setNewGoal({
      ...latestGoal,
      // Explicitly set all fields to ensure they're in the form state
      priority: latestGoal.priority,
      type: latestGoal.type,
      name: latestGoal.name,
      targetAmount: latestGoal.targetAmount,
      currentAmount: latestGoal.currentAmount,
      monthlyContribution: latestGoal.monthlyContribution,
      allocatedPercentage: latestGoal.allocatedPercentage,
      deadline: latestGoal.deadline,
      apr: latestGoal.apr,
      originalTargetAmount: latestGoal.originalTargetAmount,
      loanTermYears: latestGoal.loanTermYears,
      loanTermMonths: latestGoal.loanTermMonths
    });
    // Set the APR input value for display
    setAprInputValue(latestGoal.apr ? latestGoal.apr.toString() : '');
    setModalVisible(true);
  };

  // Add validation to form input handlers
  const handleTypeSelection = (type: Goal['type']) => {
    setNewGoal(prev => {
      const updated = {
        ...prev,
        type,
        // Only reset APR and related fields when switching types for new goals
        // Don't reset when editing existing goals
        ...(editingGoal ? {} : {
          apr: undefined,
          originalTargetAmount: undefined,
          loanTermYears: undefined,
          loanTermMonths: undefined,
        })
      };
      // Only clear APR input value for new goals
      if (!editingGoal) {
        setAprInputValue('');
      }
      return updated;
    });
  };

  const handlePrioritySelection = (priority: PriorityType) => {
    console.log('Setting priority:', priority);
    setNewGoal(prev => {
      const updated = {
        ...prev,
        priority
      };
      console.log('Updated goal state:', updated);
      return updated;
    });
  };

  const handleAddPayment = async () => {
    console.log('=== GOALS SCREEN: handleAddPayment called ===', { 
      selectedGoal: selectedGoal?.name, 
      selectedGoalId: selectedGoal?.id,
      newPayment,
      goalsCount: goals.length
    });
    
    if (!selectedGoal || !newPayment.amount || !newPayment.date) {
      console.log('Missing payment information:', {
        hasSelectedGoal: !!selectedGoal, 
        amount: newPayment.amount, 
        date: newPayment.date 
      });
      return;
    }

    try {
      const amount = Number(newPayment.amount);
      if (isNaN(amount) || amount <= 0) {
        console.log('Invalid amount:', amount);
        return;
      }

      const previousAmount = selectedGoal.currentAmount;

      console.log('=== GOALS SCREEN: About to call context addPayment ===', {
        goalId: selectedGoal.id,
        payment: {
          amount: amount,
          date: newPayment.date,
          note: newPayment.note || ''
        }
      });

      await addPayment(selectedGoal.id, {
        amount: amount,
        date: newPayment.date,
        note: newPayment.note || ''
      });

      console.log('=== GOALS SCREEN: Context addPayment completed successfully ===');

      setPaymentModalVisible(false);

      const updatedGoal: Goal = {
        ...selectedGoal,
        currentAmount: previousAmount + amount,
        payments: [
          ...selectedGoal.payments,
          {
            id: Date.now().toString(),
            amount,
            date: newPayment.date,
            note: newPayment.note || '',
          },
        ],
      };

      setPaymentSuccessGoalId(updatedGoal.id);
      setTimeout(() => setPaymentSuccessGoalId(null), 3000);

      if (settings.notifications.goalProgress) {
        await NotificationService.maybeNotifyGoalProgressMilestone(
          updatedGoal,
          previousAmount
        );
      }

      if (settings.notifications.paymentInput) {
        await NotificationService.cancelGoalNotifications(updatedGoal.id);
        await NotificationService.schedulePaymentReminderNotification(updatedGoal);
      }

      setNewPayment({
        amount: 0,
        date: getLocalDateString(),
        note: ''
      });
      setSelectedGoal(null);
    } catch (error) {
      console.error('Error adding payment:', error);
      Alert.alert(
        'Error',
        error instanceof Error 
          ? `Failed to add payment: ${error.message}`
          : 'Failed to add payment. Please try again.'
      );
    }
  };

  const togglePaymentHistory = (goalId: string) => {
    setExpandedGoals(prev => ({
      ...prev,
      [goalId]: !prev[goalId]
    }));
  };

  const handleDeleteGoal = (goalId: string) => {
    console.log('Delete button clicked for goal:', goalId);
    setGoalToDelete(goalId);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!goalToDelete) return;
    
    try {
      console.log('Confirming delete for goal:', goalToDelete);
      await deleteGoal(goalToDelete);
      console.log('Goal deleted successfully');
    } catch (error) {
      console.error('Error deleting goal:', error);
      Alert.alert('Error', 'Failed to delete goal. Please try again.');
    } finally {
      setDeleteModalVisible(false);
      setGoalToDelete(null);
    }
  };

  const handleDeletePayment = async () => {
    console.log('=== GOALS SCREEN: handleDeletePayment called ===', {
      paymentToDelete,
      goalsCount: goals.length
    });
    
    if (!paymentToDelete) return;

    try {
      console.log('=== GOALS SCREEN: About to call context deletePayment ===', {
        goalId: paymentToDelete.goalId,
        paymentId: paymentToDelete.paymentId
      });

      await deletePayment(paymentToDelete.goalId, paymentToDelete.paymentId);
      
      console.log('=== GOALS SCREEN: Context deletePayment completed successfully ===');
      
      // Show inline success message
      const updatedGoal = goals.find(g => g.id === paymentToDelete.goalId);
      if (updatedGoal) {
        setPaymentSuccessGoalId(updatedGoal.id);
        setTimeout(() => setPaymentSuccessGoalId(null), 3000);
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      Alert.alert('Error', 'Failed to delete payment. Please try again.');
    } finally {
      setDeletePaymentModalVisible(false);
      setPaymentToDelete(null);
    }
  };

  const renderPaymentHistory = (goal: Goal) => {
    if (!expandedGoals[goal.id]) {
      return null;
    }

    if (!goal.payments.length) {
      return (
        <Text style={styles.noPaymentsText}>No payments recorded yet</Text>
      );
    }

    return (
      <View style={styles.paymentsContainer}>
        {goal.payments
          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
          .map(payment => (
            <View key={payment.id} style={styles.paymentItem}>
              <View style={styles.paymentHeader}>
                <View style={styles.paymentInfo}>
                  <Text style={styles.paymentAmount}>
                    +{formatCurrency(payment.amount, settings.currency.code, settings.currency.symbol)}
                  </Text>
                  <Text style={styles.paymentDate}>
                    {formatDate(payment.date, settings.dateFormat)}
                  </Text>
                </View>
                <TouchableOpacity
                  style={styles.deletePaymentButton}
                  onPress={() => {
                    setPaymentToDelete({ goalId: goal.id, paymentId: payment.id });
                    setDeletePaymentModalVisible(true);
                  }}
                >
                  <Ionicons name="trash-outline" size={20} color={colors.error.main} />
                </TouchableOpacity>
              </View>
              {payment.note && (
                <Text style={styles.paymentNote}>{payment.note}</Text>
              )}
            </View>
          ))}
      </View>
    );
  };

  const renderProgressBar = (currentAmount: number, targetAmount: number) => {
    const progress = (currentAmount / targetAmount) * 100;
    return (
      <View style={styles.progressContainer}>
        <View 
          style={[
            styles.progressBar, 
            { width: `${Math.min(progress, 100)}%` }
          ]} 
        />
      </View>
    );
  };

  const renderGoalItem = ({ item }: { item: Goal }) => {
    // Debug logging for credit card goals
    if (item.type === 'Debt' && item.loanTermYears === undefined) {
      console.log('Credit Card Goal Display Debug:', {
        goalName: item.name,
        savedMonthlyContribution: item.monthlyContribution,
        savedTargetAmount: item.targetAmount,
        savedOriginalTargetAmount: item.originalTargetAmount,
        savedCurrentAmount: item.currentAmount,
        savedApr: item.apr,
        savedAllocatedPercentage: item.allocatedPercentage,
        savedDeadline: item.deadline,
        paymentsCount: item.payments.length
      });
    }

    // Use saved values directly. No more recalculations here.
    let displayMonthlyPayment = item.monthlyContribution;
    let displayTargetAmount = item.targetAmount;
    let displayTotalInterest = (item.type === 'Debt' && item.apr && item.originalTargetAmount && item.targetAmount)
      ? item.targetAmount - item.originalTargetAmount
      : 0;
    let displayDeadline = item.deadline;

    // For credit card goals, use the same calculation logic as the form
    if (item.type === 'Debt' && item.loanTermYears === undefined && item.apr && item.originalTargetAmount) {
      const P = Number(item.originalTargetAmount) || 0;
      const paid = Number(item.currentAmount) || 0;
      const apr = Number(item.apr) || 0;
      
      console.log('Credit Card Goal Card Calculation:', {
        goalName: item.name,
        originalAmount: P,
        amountPaid: paid,
        apr: apr,
        remainingBalance: P - paid,
        savedDeadline: item.deadline
      });
      
      const creditCardCalc = calculateCreditCardGoal(P, paid, apr, income.monthlyIncome, item.payments, item.createdAt);
      
      // Use calculated values for display to ensure consistency with form
      displayMonthlyPayment = creditCardCalc.recommendedMonthlyPayment;
      displayTargetAmount = creditCardCalc.targetAmount;
      displayTotalInterest = creditCardCalc.totalInterest;
      // Use the recalculated deadline to show updated due date when payments are added/removed
      displayDeadline = creditCardCalc.payoffDate;
      
      console.log('Credit Card Goal Card Calculation Results:', {
        goalName: item.name,
        displayMonthlyPayment,
        displayTargetAmount,
        displayTotalInterest,
        displayDeadline,
        savedDeadline: item.deadline
      });
    }

    // For fixed term loans, use calculated values, but only if the goal is not yet complete.
    if (!item.completedDate) {
    const fixedTermValues = calculateFixedTermValues(item);
      if (item.type === 'Debt' && item.loanTermYears !== undefined && fixedTermValues) {
        displayMonthlyPayment = fixedTermValues.monthlyPayment;
        displayTargetAmount = fixedTermValues.targetAmount;
        displayTotalInterest = fixedTermValues.totalInterest;
        displayDeadline = fixedTermValues.payoffDate;
      }
    }

    // Debug logging for final display values
    if (item.type === 'Debt' && item.loanTermYears === undefined) {
      console.log('Credit Card Goal Final Display Values:', {
        goalName: item.name,
        displayMonthlyPayment,
        displayTargetAmount,
        displayTotalInterest,
        displayDeadline,
        savedDeadline: item.deadline,
        isUsingRecalculatedDeadline: displayDeadline !== item.deadline
      });
    }

    // Calculate progress using display values
    let progressPercentage = 0;
    
    if (item.type === 'Debt' && item.loanTermYears === undefined && item.originalTargetAmount) {
      // For credit card debt, progress is based on how much has been paid off
      const originalAmount = Number(item.originalTargetAmount) || 0;
      const amountPaid = Number(item.currentAmount) || 0;
      progressPercentage = originalAmount > 0 ? Math.min((amountPaid / originalAmount) * 100, 100) : 0;
    } else {
      // For other goal types, use the standard progress calculation
      progressPercentage = displayTargetAmount && !isNaN(displayTargetAmount) && displayTargetAmount > 0
        ? Math.min((item.currentAmount / displayTargetAmount) * 100, 100)
        : 0;
    }
    
    const progress = new Animated.Value(progressPercentage);
    const progressAnimation = progress.interpolate({
      inputRange: [0, 100],
      outputRange: ['0%', `${progressPercentage}%`],
    });

    // Calculate total interest paid based on payments with daily interest
    const totalInterestPaid = item.type === 'Debt' && item.apr && item.originalTargetAmount && item.payments?.length > 0
        ? (() => {
            // Sort payments by date
            const sortedPayments = [...item.payments].sort((a, b) => 
                new Date(a.date).getTime() - new Date(b.date).getTime()
            );

            let totalInterestPaid = 0;
            let currentBalance = item.originalTargetAmount;
            let lastPaymentDate: Date | null = null;
            let firstPaymentOfDay = true;

            // Calculate daily interest rate
            const dailyRate = item.apr / 100 / 365;

            console.log('Starting interest paid calculation:', {
                goalName: item.name,
                originalBalance: currentBalance,
                dailyRate,
                numPayments: sortedPayments.length,
                payments: sortedPayments
            });

            sortedPayments.forEach((payment, index) => {
                const paymentDate = new Date(payment.date);
                
                // Check if this is a new day
                if (lastPaymentDate && paymentDate.getTime() !== lastPaymentDate.getTime()) {
                    firstPaymentOfDay = true;
                }

                // If this is not the first payment ever, calculate interest
                if (lastPaymentDate) {
                    const daysDiff = Math.floor(
                        (paymentDate.getTime() - lastPaymentDate.getTime()) / (1000 * 60 * 60 * 24)
                    );
                    
                    // Add accumulated interest for days between payments
                    if (daysDiff > 0) {
                        const interestAccumulated = currentBalance * dailyRate * daysDiff;
                        currentBalance += interestAccumulated;
                        console.log(`Payment ${index + 1} - Interest accumulated:`, {
                            daysBetweenPayments: daysDiff,
                            interestAccumulated,
                            newBalance: currentBalance
                        });
                    }
                }

                // Calculate how much of the payment goes to interest vs principal
                const dailyInterest = currentBalance * dailyRate;
                const interestPortion = firstPaymentOfDay ? dailyInterest : 0;

                totalInterestPaid += interestPortion;
                
                console.log(`Payment ${index + 1} details:`, {
                    paymentAmount: payment.amount,
                    date: payment.date,
                    dailyInterest,
                    interestPortion,
                    isFirstPaymentOfDay: firstPaymentOfDay,
                    totalInterestPaidSoFar: totalInterestPaid,
                    balanceBeforePayment: currentBalance,
                    balanceAfterPayment: Math.max(0, currentBalance - payment.amount)
                });

                // Update the balance and tracking variables
                currentBalance = Math.max(0, currentBalance - payment.amount);
                lastPaymentDate = paymentDate;
                firstPaymentOfDay = false;
            });

            console.log('Final interest paid calculation:', {
                goalName: item.name,
                totalInterestPaid,
                finalBalance: currentBalance
            });

            return totalInterestPaid;
        })()
        : 0;

    return (
      <TouchableOpacity
        style={styles.goalItem}
        onPress={() => handleEditGoal(item)}
      >
        <View style={styles.goalHeader}>
          <View style={styles.goalTitleContainer}>
            <Text style={styles.goalIcon}>{goalTypeIcons[item.type]}</Text>
            <Text style={styles.goalName}>{item.name}</Text>
          </View>
          <View style={styles.headerActions}>
            <View style={[styles.priorityBadge, { backgroundColor: priorityColors[item.priority as PriorityType] }]}>
              <Text style={styles.priorityText}>{item.priority}</Text>
            </View>
            <TouchableOpacity
              style={styles.deleteButton}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              onPress={(e) => {
                e.stopPropagation();
                handleDeleteGoal(item.id);
              }}
            >
              <Ionicons name="trash-outline" size={24} color={colors.error.main} />
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.progressContainer}>
          <Animated.View 
            style={[
              styles.progressBar,
              { width: progressAnimation }
            ]} 
          />
          <Text style={styles.progressText}>{progressPercentage.toFixed(2)}%</Text>
        </View>

        <View style={styles.goalDetails}>
          <View style={styles.detailRow}>
            <View>
              <Text style={styles.amountLabel}>Current</Text>
              <Text style={styles.amountValue}>
                {formatCurrency(item.currentAmount, settings.currency.code, settings.currency.symbol)}
              </Text>
            </View>
            <View style={styles.targetContainer}>
              <Text style={styles.amountLabel}>Target</Text>
              <Text style={styles.amountValue}>
                {formatCurrency(displayTargetAmount, settings.currency.code, settings.currency.symbol)}
              </Text>
              {displayTotalInterest > 0 && (
                <>
                  <Text style={styles.interestNote}>
                    Interest: {formatCurrency(displayTotalInterest, settings.currency.code, settings.currency.symbol)}
                  </Text>
                  {totalInterestPaid > 0 && (
                    <Text style={styles.interestPaidNote}>
                      Interest Paid: {formatCurrency(totalInterestPaid, settings.currency.code, settings.currency.symbol)}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>
          <View style={styles.detailRow}>
            <View>
              <Text style={styles.amountLabel}>Monthly</Text>
              <Text style={styles.amountValue}>
                {formatCurrency(displayMonthlyPayment, settings.currency.code, settings.currency.symbol)}
              </Text>
            </View>
            <View style={styles.targetContainer}>
              {/* Interest paid is now shown only in the target amount section above */}
            </View>
          </View>
        </View>

        {displayDeadline && (
          <View style={styles.goalDeadline}>
            <Ionicons name="calendar-outline" size={16} color={colors.text.secondary} />
            <Text style={styles.deadlineText}>
              Due by {formatDate(displayDeadline, settings.dateFormat)}
            </Text>
          </View>
        )}

        <View style={styles.actionButtons}>
          <TouchableOpacity
            style={styles.addPaymentButton}
            onPress={(e) => {
              e.stopPropagation();
              setSelectedGoal(item);
              setPaymentModalVisible(true);
            }}
          >
            <Ionicons name="add-circle-outline" size={20} color={colors.primary.main} />
            <Text style={styles.addPaymentText}>Add Payment</Text>
          </TouchableOpacity>

          {item.payments.length > 0 && (
            <TouchableOpacity
              style={styles.toggleHistoryButton}
              onPress={(e) => {
                e.stopPropagation();
                togglePaymentHistory(item.id);
              }}
            >
              <Ionicons 
                name={expandedGoals[item.id] ? "chevron-up" : "chevron-down"} 
                size={20} 
                color={colors.text.secondary} 
              />
              <Text style={styles.toggleHistoryText}>
                {expandedGoals[item.id] ? "Hide History" : `Show History (${item.payments.length})`}
              </Text>
            </TouchableOpacity>
          )}
        </View>

        {renderPaymentHistory(item)}
        {paymentSuccessGoalId === item.id && (
          <Text style={styles.successText}>Payment recorded successfully!</Text>
        )}
      </TouchableOpacity>
    );
  };

  const renderFixedTermLoanFields = () => {
    const P = Number(newGoal.originalTargetAmount) || 0;
    const r = (Number(newGoal.apr) || 0) / 100 / 12;
    const n = (Number(newGoal.loanTermYears) || 0) * 12 + (Number(newGoal.loanTermMonths) || 0);
    
    const calculateMonthlyPayment = () => {
      if (P > 0 && r > 0 && n > 0) {
        return Math.round((P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1)) * 100) / 100;
      }
      return 0;
    };

    const calculateMonthlyAllocation = () => {
      if (P > 0 && r > 0 && n > 0 && income.monthlyIncome > 0) {
        const monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        return Math.round(((monthlyPayment / income.monthlyIncome) * 100) * 100) / 100;
      }
      return 0;
    };

    const calculateTargetAmount = () => {
      if (P > 0 && r > 0 && n > 0) {
        const monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const totalPaid = monthlyPayment * n;
        const totalInterest = totalPaid - P;
        return Math.round((P + totalInterest) * 100) / 100;
      }
      return 0;
    };

    const renderTargetAmountLabel = () => {
      if (P > 0 && r > 0 && n > 0) {
        const monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
        const totalPaid = monthlyPayment * n;
        const totalInterest = totalPaid - P;
        const targetAmount = P + totalInterest;
        return targetAmount > 0 ? <Text style={styles.inputLabel}>Target Amount (Auto-Calculated)</Text> : null;
      }
      return null;
    };

    const calculateCompletionDate = () => {
      const years = Number(newGoal.loanTermYears) || 0;
      const months = Number(newGoal.loanTermMonths) || 0;
      const totalMonths = years * 12 + months;
      if (totalMonths > 0) {
        const estimatedDate = new Date();
        estimatedDate.setMonth(estimatedDate.getMonth() + totalMonths);
        return estimatedDate.toISOString().split('T')[0];
      }
      return '';
    };

    const renderCompletionDateLabel = () => {
      const years = Number(newGoal.loanTermYears) || 0;
      const months = Number(newGoal.loanTermMonths) || 0;
      const totalMonths = years * 12 + months;
      if (totalMonths > 0) {
        return <Text style={styles.inputLabel}>Estimated Completion Date (Auto-Calculated)</Text>;
      }
      return null;
    };

    return (
      <View>
        <Text style={styles.label}>Loan Terms</Text>
        <View style={styles.loanTermContainer}>
          <View style={styles.termInput}>
            <TextInput
              style={getStyleWithError(styles.termInputField, validationErrors.loanTermYears, styles.inputError)}
              placeholder="Years"
              keyboardType="numeric"
              value={newGoal.loanTermYears?.toString() || ''}
              onChangeText={(text) => {
                const years = parseInt(text) || 0;
                handleLoanTermChange('years', years);
                if (validationErrors.loanTermYears) {
                  setValidationErrors(prev => ({ ...prev, loanTermYears: '' }));
                }
              }}
            />
            {(newGoal.loanTermYears !== undefined && newGoal.loanTermYears !== null) ? <Text style={styles.termLabel}>Years</Text> : null}
            {validationErrors.loanTermYears ? <Text style={styles.errorText}>{validationErrors.loanTermYears}</Text> : null}
          </View>
          <View style={styles.termInput}>
            <TextInput
              style={styles.termInputField}
              placeholder="Months"
              keyboardType="numeric"
              value={newGoal.loanTermMonths?.toString()}
              onChangeText={(text) => handleLoanTermChange('months', Number(text))}
            />
            <Text style={styles.termLabel}>Months</Text>
          </View>
        </View>
        <TextInput
          style={getStyleWithError(styles.input, validationErrors.originalTargetAmount, styles.inputError)}
          placeholder="Original Amount"
          keyboardType="numeric"
          value={newGoal.originalTargetAmount?.toString()}
          onChangeText={(text) => {
            const amount = parseCurrencyInput(text);
            handleAmountChange('originalTargetAmount', amount);
            if (validationErrors.originalTargetAmount) {
              setValidationErrors(prev => ({ ...prev, originalTargetAmount: '' }));
            }
          }}
        />
        {validationErrors.originalTargetAmount ? <Text style={styles.errorText}>{validationErrors.originalTargetAmount}</Text> : null}
        {newGoal.originalTargetAmount ? <Text style={styles.inputLabel}>Original Amount</Text> : null}
        <TextInput 
          style={styles.input} 
          placeholder="Start Date" 
          value={newGoal.startDate} 
          onChangeText={(text) => setNewGoal(prev => ({ ...prev, startDate: text }))} 
        />
        {newGoal.startDate ? <Text style={styles.inputLabel}>Start Date</Text> : null}
        <TextInput
          style={getStyleWithError(styles.input, validationErrors.currentAmount, styles.inputError)}
          placeholder="Total Paid to Date"
          keyboardType="numeric"
          value={newGoal.currentAmount?.toString()}
          onChangeText={(text) => {
            const amount = parseCurrencyInput(text);
            handleAmountChange('currentAmount', amount);
            if (validationErrors.currentAmount) {
              setValidationErrors(prev => ({ ...prev, currentAmount: '' }));
            }
          }}
        />
        {validationErrors.currentAmount ? <Text style={styles.errorText}>{validationErrors.currentAmount}</Text> : null}
        {newGoal.currentAmount ? <Text style={styles.inputLabel}>Total Paid to Date</Text> : null}
        <TextInput 
          style={getStyleWithError(styles.input, validationErrors.apr, styles.inputError)} 
          placeholder="Interest Rate (APR %)" 
          keyboardType="numeric" 
          value={aprInputValue} 
          onChangeText={(text) => { 
            setAprInputValue(text); 
            const apr = parseFloat(text); 
            handleAprChange(isNaN(apr) ? 0 : apr); 
            if (validationErrors.apr) { 
              setValidationErrors(prev => ({ ...prev, apr: '' })); 
            } 
          }} 
        />
        {validationErrors.apr ? <Text style={styles.errorText}>{validationErrors.apr}</Text> : null}
        {aprInputValue ? <Text style={styles.inputLabel}>Interest Rate (APR %)</Text> : null}
        <TextInput
          style={[styles.input, styles.autoCalculatedInput]}
          placeholder="Monthly Payment (Auto-Calculated)"
          value={calculateMonthlyPayment() ? formatCurrency(calculateMonthlyPayment(), settings.currency.code, settings.currency.symbol) : ''}
          editable={false}
        />
        {calculateMonthlyPayment() ? <Text style={styles.inputLabel}>Monthly Payment (Auto-Calculated)</Text> : null}
        <TextInput
          style={[styles.input, styles.autoCalculatedInput]}
          placeholder="Monthly Allocation % (Auto-Calculated)"
          value={calculateMonthlyAllocation() ? `${calculateMonthlyAllocation().toFixed(2)}%` : ''}
          editable={false}
        />
        {calculateMonthlyAllocation() ? <Text style={styles.inputLabel}>Monthly Allocation % (Auto-Calculated)</Text> : null}
        <TextInput
          style={[styles.input, styles.autoCalculatedInput]}
          placeholder="Target Amount (Auto-Calculated)"
          value={calculateTargetAmount() ? formatCurrency(calculateTargetAmount(), settings.currency.code, settings.currency.symbol) : ''}
          editable={false}
        />
        {renderTargetAmountLabel()}
        <TextInput
          style={[styles.input, styles.autoCalculatedInput]}
          placeholder="Estimated Completion Date (Auto-Calculated)"
          value={calculateCompletionDate()}
          editable={false}
        />
        {renderCompletionDateLabel()}
      </View>
    );
  };

  useFocusEffect(
    React.useCallback(() => {
      // @ts-ignore
      if (route.params && route.params.openAddGoalModal) {
        // Check goal limit before opening modal
        if (!canCreateGoal(goals.length)) {
          setGoalLimitModalVisible(true);
        } else {
          setEditingGoal(null);
          setNewGoal({});
          setModalVisible(true);
        }
        // @ts-ignore
        navigation.setParams({ openAddGoalModal: false });
      }
    }, [route.params, goals.length])
  );

  // Custom handler for adding a goal with subscription limit check
  const handleAddGoalWithLimitCheck = async (goalData: Partial<Goal>) => {
    if (!canCreateGoal(goals.length)) {
      setGoalLimitModalVisible(true);
      return;
    }
    // Proceed with normal add goal logic (e.g., open modal or call addGoal)
    setNewGoal(goalData);
    setModalVisible(true);
  };

  const goalsListKey = `goals-list-${goals.length}-${goals.map(g => g.currentAmount).join('-')}-${goals.map(g => g.deadline).join('-')}-${formUpdateKey}`;

  const goalsListEmpty = (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyText}>
        {hasActiveFilters
          ? 'No goals match your filters'
          : 'No goals yet. Add your first goal!'}
      </Text>
    </View>
  );

  const addGoalHeaderAction = isWeb ? (
    <TouchableOpacity
      style={styles.addButtonHeader}
      onPress={() => handleAddGoalWithLimitCheck({})}
      accessibilityRole="button"
      accessibilityLabel="Add goal"
    >
      <Ionicons name="add" size={26} color="white" />
    </TouchableOpacity>
  ) : undefined;

  return (
    <WebPage title="My Goals" headerAction={addGoalHeaderAction}>
    <SafeAreaView style={styles.container}>
      <GoalSearchFilters
        goals={goals}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        selectedTypeFilter={selectedTypeFilter}
        onTypeFilterChange={setSelectedTypeFilter}
        selectedPriorityFilter={selectedPriorityFilter}
        onPriorityFilterChange={setSelectedPriorityFilter}
        selectedLoanTypeFilter={selectedLoanTypeFilter}
        onLoanTypeFilterChange={setSelectedLoanTypeFilter}
        selectedStatusFilter={selectedStatusFilter}
        onStatusFilterChange={setSelectedStatusFilter}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        onClearFilters={clearFilters}
      />

      {isWeb ? (
        <PageScroll key={goalsListKey} contentContainerStyle={styles.listContainer}>
          {filteredGoals.length === 0
            ? goalsListEmpty
            : filteredGoals.map((item) => (
                <React.Fragment key={item.id}>{renderGoalItem({ item })}</React.Fragment>
              ))}
        </PageScroll>
      ) : (
        <FlatList
          key={goalsListKey}
          data={filteredGoals}
          renderItem={renderGoalItem}
          keyExtractor={(item) => item.id}
          style={styles.goalsList}
          contentContainerStyle={styles.listContainer}
          nestedScrollEnabled
          ListHeaderComponent={null}
          ListEmptyComponent={goalsListEmpty}
        />
      )}

      {/* Ad Banner */}
      <AdBanner />

      {!isWeb ? (
        <TouchableOpacity
          style={styles.addButton}
          onPress={() => {
            handleAddGoalWithLimitCheck({});
          }}
        >
          <Ionicons name="add" size={30} color="white" />
        </TouchableOpacity>
      ) : null}

      <AppModal
        animationType="slide"
        visible={modalVisible}
        size="lg"
        onRequestClose={() => {
          console.log('Modal onRequestClose triggered');
          setModalVisible(false);
          setTimeout(() => {
          setNewGoal({});
          setEditingGoal(null);
          setValidationErrors({});
          }, 100);
        }}
      >
            <Text style={styles.modalTitle}>
              {editingGoal ? 'Edit Goal' : 'Add New Goal'}
            </Text>
            
            <TextInput
              style={getStyleWithError(styles.input, validationErrors.name, styles.inputError)}
              placeholder="Goal Name"
              value={newGoal.name}
              onChangeText={(text) => {
                setNewGoal({ ...newGoal, name: text });
                if (validationErrors.name) {
                  setValidationErrors(prev => ({ ...prev, name: '' }));
                }
              }}
            />
            {validationErrors.name ? <Text style={styles.errorText}>{validationErrors.name}</Text> : null}
            {newGoal.name ? <Text style={styles.inputLabel}>Goal Name</Text> : null}

            <Text style={styles.label}>Goal Type</Text>
            <View style={styles.typeContainer}>
              {Object.entries(goalTypeIcons).map(([type, icon]) => (
                <TouchableOpacity
                  key={type}
                  style={[
                    styles.typeButton,
                    newGoal.type === type && styles.typeButtonSelected,
                  ]}
                  onPress={() => {
                    handleTypeSelection(type as Goal['type']);
                    if (validationErrors.type) {
                      setValidationErrors(prev => ({ ...prev, type: '' }));
                    }
                  }}
                >
                  <Text style={styles.typeIcon}>{icon}</Text>
                  <Text style={[
                    styles.typeText,
                    newGoal.type === type && styles.typeTextSelected,
                  ]}>
                    {type}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {validationErrors.type ? <Text style={styles.errorText}>{validationErrors.type}</Text> : null}

            <Text style={styles.label}>Priority</Text>
            <View style={styles.priorityContainer}>
              {(Object.keys(priorityColors) as PriorityType[]).map((priority) => (
                <TouchableOpacity
                  key={priority}
                  style={[
                    styles.priorityButton,
                    newGoal.priority === priority && styles.priorityButtonSelected,
                    { borderColor: priorityColors[priority] }
                  ]}
                  onPress={() => {
                    handlePrioritySelection(priority);
                    if (validationErrors.priority) {
                      setValidationErrors(prev => ({ ...prev, priority: '' }));
                    }
                  }}
                >
                  <Text style={[
                    styles.priorityButtonText,
                    { color: priorityColors[priority] }
                  ]}>
                    {priority}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
            {validationErrors.priority ? <Text style={styles.errorText}>{validationErrors.priority}</Text> : null}

            {newGoal.type === 'Debt' && (
              <View>
                <Text style={styles.label}>Loan Type</Text>
                <View style={styles.loanTypeContainer}>
                  <TouchableOpacity
                    style={[styles.loanTypeButton, !newGoal.loanTermYears && styles.loanTypeButtonSelected]}
                    onPress={() => {
                      setNewGoal(prev => ({ 
                        ...prev, 
                        loanTermYears: undefined, 
                        loanTermMonths: undefined,
                        // Only clear APR for new goals, not when editing
                        ...(editingGoal ? {} : { apr: undefined })
                      }));
                      // Only clear APR input for new goals
                      if (!editingGoal) {
                        setAprInputValue('');
                      }
                    }}
                  >
                    <Text style={[styles.loanTypeText, !newGoal.loanTermYears && styles.loanTypeTextSelected]}>Credit Card</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[styles.loanTypeButton, newGoal.loanTermYears !== undefined && styles.loanTypeButtonSelected, styles.loanTypeButtonLast]}
                    onPress={() => {
                      setNewGoal(prev => ({ 
                        ...prev, 
                        loanTermYears: 1, 
                        loanTermMonths: 0,
                        // Only clear APR for new goals, not when editing
                        ...(editingGoal ? {} : { apr: undefined })
                      }));
                      // Only clear APR input for new goals
                      if (!editingGoal) {
                        setAprInputValue('');
                      }
                    }}
                  >
                    <Text style={[styles.loanTypeText, newGoal.loanTermYears !== undefined && styles.loanTypeTextSelected]}>Fixed Term Loan</Text>
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {newGoal.type === 'Debt' && newGoal.loanTermYears === undefined && (
              <View key={`credit-card-fields-${formUpdateKey}`}>
                {renderCreditCardFields()}
              </View>
            )}

            {newGoal.type === 'Debt' && newGoal.loanTermYears !== undefined && (
              <View>
                {renderFixedTermLoanFields()}
              </View>
            )}

            {/* Savings/Investment fields block - DO NOT TOUCH DEBT OR OTHER GOAL TYPES */}
            {(newGoal.type === 'Savings' || newGoal.type === 'Investment') && (
              <ModalFormGrid>
                <ModalFormField>
                <TextInput
                  style={getStyleWithError(styles.input, validationErrors.targetAmount, styles.inputError)}
                  placeholder="Target Amount"
                  keyboardType="numeric"
                  value={newGoal.targetAmount?.toString()}
                  onChangeText={text => {
                    const amount = parseCurrencyInput(text);
                    handleAmountChange('targetAmount', amount);
                    if (validationErrors.targetAmount) {
                      setValidationErrors(prev => ({ ...prev, targetAmount: '' }));
                    }
                  }}
                />
                {validationErrors.targetAmount ? <Text style={styles.errorText}>{validationErrors.targetAmount}</Text> : null}
                {newGoal.targetAmount ? <Text style={styles.inputLabel}>Target Amount</Text> : null}
                </ModalFormField>
                <ModalFormField>
                <TextInput
                  style={styles.input}
                  placeholder="Start Date"
                  value={newGoal.startDate}
                  onChangeText={text => setNewGoal(prev => ({ ...prev, startDate: text }))}
                />
                {newGoal.startDate ? <Text style={styles.inputLabel}>Start Date</Text> : null}
                </ModalFormField>
                <ModalFormField>
                <TextInput
                  style={getStyleWithError(styles.input, validationErrors.currentAmount, styles.inputError)}
                  placeholder="Total Paid to Date"
                  keyboardType="numeric"
                  value={newGoal.currentAmount?.toString()}
                  onChangeText={(text) => {
                    const amount = parseCurrencyInput(text);
                    handleAmountChange('currentAmount', amount);
                    if (validationErrors.currentAmount) {
                      setValidationErrors(prev => ({ ...prev, currentAmount: '' }));
                    }
                  }}
                />
                {validationErrors.currentAmount ? <Text style={styles.errorText}>{validationErrors.currentAmount}</Text> : null}
                {newGoal.currentAmount ? <Text style={styles.inputLabel}>Total Paid to Date</Text> : null}
                </ModalFormField>
                <ModalFormField>
                <TextInput
                  style={getStyleWithError(styles.input, validationErrors.monthlyContribution, styles.inputError)}
                  placeholder="Monthly Payment"
                  keyboardType="numeric"
                  value={newGoal.monthlyContribution?.toString()}
                  onChangeText={text => {
                    handleMonthlyContributionChange(Number(text));
                    if (validationErrors.monthlyContribution) {
                      setValidationErrors(prev => ({ ...prev, monthlyContribution: '' }));
                    }
                  }}
                />
                {validationErrors.monthlyContribution ? <Text style={styles.errorText}>{validationErrors.monthlyContribution}</Text> : null}
                {newGoal.monthlyContribution ? <Text style={styles.inputLabel}>Monthly Payment</Text> : null}
                </ModalFormField>
                <ModalFormField>
                <TextInput
                  style={[styles.input, styles.autoCalculatedInput]}
                  placeholder="Monthly Allocation % (Auto-Calculated)"
                  value={income.monthlyIncome > 0 && newGoal.monthlyContribution ? `${((newGoal.monthlyContribution / income.monthlyIncome) * 100).toFixed(2)}%` : ''}
                  editable={false}
                />
                {income.monthlyIncome > 0 && newGoal.monthlyContribution ? <Text style={styles.inputLabel}>Monthly Allocation % (Auto-Calculated)</Text> : null}
                </ModalFormField>
                <ModalFormField>
                <TextInput
                  style={[styles.input, styles.autoCalculatedInput]}
                  placeholder="Estimated Completion Date (Auto-Calculated)"
                  value={newGoal.deadline || undefined}
                  editable={false}
                />
                {newGoal.deadline ? <Text style={styles.inputLabel}>Estimated Completion Date (Auto-Calculated)</Text> : null}
                </ModalFormField>
              </ModalFormGrid>
            )}

            <View style={[styles.buttonContainer, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.button, styles.cancelButton]}
                onPress={() => {
                  console.log('Cancel button clicked');
                  setModalVisible(false);
                  setNewGoal({});
                  setEditingGoal(null);
                  setValidationErrors({});
                }}
              >
                <Text style={[styles.buttonText, { color: 'white' }]}>Cancel</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.button, styles.saveButton]}
                onPress={() => {
                  console.log('Save button clicked!');
                  handleSaveGoal();
                }}
              >
                <Text style={styles.buttonText}>Save</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      <AppModal
        animationType="slide"
        visible={paymentModalVisible}
        size="md"
        scrollable={false}
        onRequestClose={() => {
          console.log('Payment modal closing');
          setPaymentModalVisible(false);
          setNewPayment({
            amount: 0,
            date: getLocalDateString(),
            note: ''
          });
          setSelectedGoal(null);
        }}
      >
            <Text style={styles.modalTitle}>Add Payment</Text>
            <Text style={styles.goalNameText}>
              Goal: {selectedGoal?.name}
            </Text>

            <View style={styles.inputContainer}>
              <Text style={styles.currencySymbol}>{settings.currency.symbol}</Text>
              <TextInput
                style={styles.input}
                placeholder="Amount"
                keyboardType="decimal-pad"
                value={newPayment.amount?.toString()}
                onChangeText={(text) => {
                  const amount = parseCurrencyInput(text);
                  setNewPayment({ ...newPayment, amount });
                }}
              />
            </View>

            <TextInput
              style={styles.input}
              placeholder="Date (YYYY-MM-DD)"
              value={newPayment.date}
              onChangeText={(text) => setNewPayment({ ...newPayment, date: text })}
            />

            <TextInput
              style={[styles.input, styles.noteInput]}
              placeholder="Note (optional)"
              multiline
              value={newPayment.note}
              onChangeText={(text) => setNewPayment({ ...newPayment, note: text })}
            />

            <View style={[styles.modalButtons, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => setPaymentModalVisible(false)}
              >
                <Text style={[styles.buttonText, { color: 'white' }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, styles.saveButton]}
                onPress={handleAddPayment}
              >
                <Text style={styles.modalButtonText}>Save</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      {/* Delete Confirmation Modal */}
      <AppModal
        visible={deleteModalVisible}
        size="sm"
        scrollable={false}
        onRequestClose={() => {
          setDeleteModalVisible(false);
          setGoalToDelete(null);
        }}
      >
            <Text style={[styles.modalTitle, { marginBottom: 16 }]}>Delete Goal</Text>
            <Text style={styles.modalText}>
              Are you sure you want to delete this goal? This action cannot be undone.
            </Text>
            <View style={[styles.modalButtons, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setDeleteModalVisible(false);
                  setGoalToDelete(null);
                }}
              >
                <Text style={[styles.buttonText, { color: 'white' }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: colors.error.main }]}
                onPress={confirmDelete}
              >
                <Text style={styles.buttonText}>Delete</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      {/* Delete Payment Confirmation Modal */}
      <AppModal
        visible={deletePaymentModalVisible}
        size="sm"
        scrollable={false}
        onRequestClose={() => {
          setDeletePaymentModalVisible(false);
          setPaymentToDelete(null);
        }}
      >
            <Text style={[styles.modalTitle, { marginBottom: 16 }]}>Delete Payment</Text>
            <Text style={styles.modalText}>
              Are you sure you want to delete this payment? This will reduce the current amount and may affect interest calculations.
            </Text>
            <View style={[styles.modalButtons, isWeb && webModalFooterStyle]}>
              <TouchableOpacity
                style={[styles.modalButton, styles.cancelButton]}
                onPress={() => {
                  setDeletePaymentModalVisible(false);
                  setPaymentToDelete(null);
                }}
              >
                <Text style={[styles.buttonText, { color: 'white' }]}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.modalButton, { backgroundColor: colors.error.main }]}
                onPress={handleDeletePayment}
              >
                <Text style={styles.buttonText}>Delete</Text>
              </TouchableOpacity>
            </View>
      </AppModal>

      <GoalLimitModal
        visible={goalLimitModalVisible}
        onClose={() => setGoalLimitModalVisible(false)}
        onUpgrade={() => {
          setGoalLimitModalVisible(false);
          navigation.navigate('Subscription');
        }}
        currentGoals={goals.length}
        maxGoals={getMaxGoals()}
      />
    </SafeAreaView>
    </WebPage>
  );
};

const useGoalsStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.goals,
  },
  goalsList: {
    flex: 1,
  },
  listContainer: {
    padding: 16,
    paddingBottom: 80,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  emptyText: {
    fontSize: 16,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  goalItem: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
  },
  goalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    width: '100%',
  },
  goalTitleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 16,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  goalIcon: {
    fontSize: 24,
    marginRight: 8,
  },
  goalName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.text.primary,
    flex: 1,
  },
  priorityBadge: {
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 12,
  },
  priorityText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  deleteButton: {
    padding: 4,
  },
  goalDetails: {
    flexDirection: 'column',
    gap: 12,
    marginBottom: 12,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  amountLabel: {
    fontSize: 12,
    color: colors.text.secondary,
    marginBottom: 4,
  },
  amountValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.text.primary,
    textAlign: 'right',
  },
  targetContainer: {
    alignItems: 'flex-end',
  },
  deadlineContainer: {
    marginBottom: 16,
  },
  addButton: {
    position: 'absolute',
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: colors.button.primary,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
  },
  addButtonHeader: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.button.primary,
    justifyContent: 'center',
    alignItems: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
  },
  modalContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    paddingHorizontal: 16,
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 24,
    width: '100%',
    maxHeight: '90%',
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 24,
    textAlign: isWeb ? 'left' : 'center',
  },
  input: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    padding: 12,
    marginBottom: 4,
    fontSize: 16,
  },
  inputLabel: {
    fontSize: 12,
    color: colors.text.secondary,
    marginBottom: 16,
    marginLeft: 4,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  typeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  typeButton: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    backgroundColor: colors.grey[100],
    marginHorizontal: 4,
  },
  typeButtonSelected: {
    backgroundColor: colors.primary.background,
  },
  typeIcon: {
    fontSize: 24,
    marginBottom: 4,
  },
  typeText: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  typeTextSelected: {
    color: colors.button.primary,
    fontWeight: 'bold',
  },
  priorityContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  priorityButton: {
    flex: 1,
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    marginHorizontal: 4,
  },
  priorityButtonSelected: {
    backgroundColor: colors.grey[100],
  },
  priorityButtonText: {
    fontSize: 14,
    fontWeight: 'bold',
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
  },
  button: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    marginHorizontal: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.grey.cancel,
  },
  saveButton: {
    backgroundColor: colors.button.primary,
  },
  buttonText: {
    color: colors.button.primaryText,
    fontSize: 16,
    fontWeight: 'bold',
  },
  actionButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  addPaymentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    borderRadius: 8,
    backgroundColor: colors.primary.background,
  },
  addPaymentText: {
    marginLeft: 4,
    color: colors.primary.main,
    fontWeight: '600',
  },
  toggleHistoryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
  },
  toggleHistoryText: {
    marginLeft: 4,
    color: colors.text.secondary,
    fontSize: 14,
  },
  paymentsContainer: {
    marginTop: 12,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingTop: 12,
  },
  paymentItem: {
    backgroundColor: colors.grey[100],
    borderRadius: 8,
    padding: 12,
    marginBottom: 8,
  },
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  paymentInfo: {
    flex: 1,
  },
  paymentAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.success.main,
  },
  paymentDate: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  paymentNote: {
    marginTop: 4,
    fontSize: 14,
    color: colors.text.secondary,
    fontStyle: 'italic',
  },
  noPaymentsText: {
    textAlign: 'center',
    color: colors.text.secondary,
    fontStyle: 'italic',
    marginTop: 12,
  },
  goalNameText: {
    fontSize: 16,
    color: colors.text.secondary,
    marginBottom: 16,
  },
  noteInput: {
    height: 80,
    textAlignVertical: 'top',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  currencySymbol: {
    fontSize: 18,
    marginRight: 8,
    color: colors.text.primary,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
  },
  modalButton: {
    flex: 1,
    padding: 16,
    borderRadius: 8,
    marginHorizontal: 8,
    alignItems: 'center',
  },
  modalButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  successMessage: {
    color: colors.success.main,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    backgroundColor: colors.surface,
    padding: 8,
    borderRadius: 8,
  },
  interestNote: {
    fontSize: 11,
    color: colors.error.main,
    marginTop: 4,
    textAlign: 'right',
  },
  interestPaidNote: {
    fontSize: 11,
    color: colors.success.main,
    marginTop: 4,
    textAlign: 'right',
  },
  progressContainer: {
    height: 8,
    backgroundColor: colors.grey[200],
    borderRadius: 4,
    marginBottom: 16,
    overflow: 'hidden',
    position: 'relative',
  },
  progressBar: {
    height: '100%',
    backgroundColor: colors.button.primary,
    borderRadius: 4,
  },
  progressText: {
    position: 'absolute',
    right: 0,
    top: 12,
    fontSize: 12,
    color: colors.text.secondary,
  },
  interestPaidText: {
    fontSize: 11,
    color: colors.success.main,
    marginTop: 4,
    textAlign: 'right',
  },
  deletePaymentButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: colors.background.paper,
  },
  loanTypeContainer: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  loanTypeButton: {
    flex: 1,
    alignItems: 'center',
    padding: 8,
    borderRadius: 8,
    backgroundColor: colors.grey[100],
    marginRight: 8,
  },
  loanTypeButtonSelected: {
    backgroundColor: colors.primary.background,
  },
  loanTypeButtonLast: {
    marginRight: 0,
  },
  loanTypeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
  loanTypeTextSelected: {
    color: colors.primary.main,
    fontWeight: 'bold',
  },
  loanTermContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    gap: 8,
  },
  termInput: {
    flex: 1,
  },
  termInputField: {
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 8,
    marginBottom: 4,
    fontSize: 16,
    textAlign: 'center',
    width: '100%',
  },
  termLabel: {
    fontSize: 12,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  autoCalculatedInput: {
    backgroundColor: colors.grey[100],
  },
  goalDeadline: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
  },
  deadlineText: {
    marginLeft: 4,
    fontSize: 14,
    color: colors.text.secondary,
  },
  modalText: {
    fontSize: 16,
    color: colors.text.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  inputError: {
    borderColor: colors.error.main,
  },
  errorText: {
    color: colors.error.main,
    fontSize: 12,
    marginBottom: 8,
    marginLeft: 4,
  },
  successText: {
    color: colors.success.main,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 16,
    backgroundColor: colors.surface,
    padding: 8,
    borderRadius: 8,
  },
}));

// After calculateDebtWithInterest
const calculateCreditCardGoal = (
  originalAmount: number,
  currentAmount: number,
  apr: number,
  monthlyIncome?: number,
  payments?: Payment[],
  createdAt?: string
) => {
  if (!originalAmount || !apr || isNaN(originalAmount) || isNaN(currentAmount) || isNaN(apr) || originalAmount <= currentAmount) {
    return {
      recommendedMonthlyPayment: 0,
      targetAmount: originalAmount,
      totalInterest: 0,
      payoffDate: new Date().toISOString().split('T')[0],
      months: 0,
      allocationPercent: 0
    };
  }
  
  const remainingBalance = originalAmount - currentAmount;
  const minPayment = Math.max(remainingBalance * 0.02, 25);
  const recommendedPayment = minPayment + (minPayment * 0.5); // 50% above minimum
  
  let balance = remainingBalance;
  let totalInterest = 0;
  let months = 0;
  const dailyRate = apr / 100 / 365;
  const maxMonths = 600;
  
  while (balance > 0.01 && months < maxMonths) {
    // Calculate daily interest for the month
    let monthInterest = 0;
    for (let d = 0; d < 30; d++) {
      const dailyInterest = balance * dailyRate;
      balance += dailyInterest;
      monthInterest += dailyInterest;
    }
    totalInterest += monthInterest;
    // Make the recommended payment (not recalculate minPayment)
    const payment = Math.min(recommendedPayment, balance);
    balance -= payment;
    months++;
  }

  // Find base date: last payment date, or createdAt, or today
  let baseDate: Date;
  if (payments && payments.length > 0) {
    const sorted = [...payments].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    baseDate = new Date(sorted[0].date);
  } else if (createdAt) {
    baseDate = new Date(createdAt);
  } else {
    baseDate = new Date();
  }
  baseDate.setHours(0,0,0,0);
  const payoff = new Date(baseDate);
  payoff.setMonth(payoff.getMonth() + months);

  return {
    recommendedMonthlyPayment: Math.round(recommendedPayment * 100) / 100,
    targetAmount: Math.round((originalAmount + totalInterest) * 100) / 100,
    totalInterest: Math.round(totalInterest * 100) / 100,
    payoffDate: payoff.toISOString().split('T')[0],
    months,
    allocationPercent: monthlyIncome ? Math.round((recommendedPayment / monthlyIncome) * 10000) / 100 : 0
  };
};

// Move this function outside renderGoalItem
const calculateFixedTermValues = (goal: Goal) => {
  if (goal.type !== 'Debt' || goal.loanTermYears === undefined || !goal.apr || !goal.originalTargetAmount) {
    return null;
  }
  const P = Number(goal.originalTargetAmount) || 0;
  const r = (Number(goal.apr) || 0) / 100 / 12;
  const n = (Number(goal.loanTermYears) || 0) * 12 + (Number(goal.loanTermMonths) || 0);
  if (P <= 0 || r <= 0 || n <= 0) {
    return null;
  }
  const monthlyPayment = P * (r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const totalPaid = monthlyPayment * n;
  const totalInterest = totalPaid - P;
  const targetAmount = P + totalInterest;
  const estimatedDate = new Date();
  estimatedDate.setMonth(estimatedDate.getMonth() + n);
  return {
    monthlyPayment: Math.round(monthlyPayment * 100) / 100,
    targetAmount: Math.round(targetAmount * 100) / 100,
    totalInterest: Math.round(totalInterest * 100) / 100,
    payoffDate: estimatedDate.toISOString().split('T')[0],
    months: n
  };
};

export default GoalsScreen;